/**
 * Section Component
 *
 * A state-based visibility component that handles different integration states.
 * This component can show/hide content based on form state changes and provides
 * a clean reactive approach to conditional rendering.
 *
 * Features:
 * - State-based visibility
 * - Form data integration
 * - Clean reactive approach
 * - Automatic state change listening
 * - CSS class customization
 *
 * @example
 * ```html
 * <uap-app-integration-settings-section
 *     state="connection_status"
 *     show-when="connected"
 *     section-type="connected">
 *     <p>Your account is connected!</p>
 * </uap-app-integration-settings-section>
 * ```
 *
 * @since 6.9.0 Initial implementation
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _AutomatorAppIntegrationSettingsSection_visible_accessor_storage, _AutomatorAppIntegrationSettingsSection_sectionType_accessor_storage, _AutomatorAppIntegrationSettingsSection_cssClass_accessor_storage, _AutomatorAppIntegrationSettingsSection_state_accessor_storage, _AutomatorAppIntegrationSettingsSection_showWhen_accessor_storage;
// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { collectFormData } from '../../utils/form-data-collector';
import { listenForEvent, removeEventListener } from '../../utils/event-manager';
// Import style for web component
import styles from './web-component-style.scss?inline';
/**
 * @element uap-app-integration-settings-section
 * @summary A state-based visibility component for integration settings.
 */
let AutomatorAppIntegrationSettingsSection = class AutomatorAppIntegrationSettingsSection extends LitElement {
    constructor() {
        super(...arguments);
        _AutomatorAppIntegrationSettingsSection_visible_accessor_storage.set(this, true);
        _AutomatorAppIntegrationSettingsSection_sectionType_accessor_storage.set(this, '');
        _AutomatorAppIntegrationSettingsSection_cssClass_accessor_storage.set(this, '');
        _AutomatorAppIntegrationSettingsSection_state_accessor_storage.set(this, '');
        _AutomatorAppIntegrationSettingsSection_showWhen_accessor_storage.set(this, '');
    }
    /**
     * Whether the section should be visible
     *
     * @type {boolean}
     */
    get visible() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsSection_visible_accessor_storage, "f"); }
    set visible(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsSection_visible_accessor_storage, value, "f"); }
    /**
     * The section type for styling
     *
     * @type {string}
     */
    get sectionType() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsSection_sectionType_accessor_storage, "f"); }
    set sectionType(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsSection_sectionType_accessor_storage, value, "f"); }
    /**
     * Additional CSS classes
     *
     * @type {string}
     */
    get cssClass() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsSection_cssClass_accessor_storage, "f"); }
    set cssClass(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsSection_cssClass_accessor_storage, value, "f"); }
    /**
     * State name to listen for
     *
     * @type {string}
     */
    get state() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsSection_state_accessor_storage, "f"); }
    set state(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsSection_state_accessor_storage, value, "f"); }
    /**
     * State value that should show this section
     *
     * @type {string}
     */
    get showWhen() { return __classPrivateFieldGet(this, _AutomatorAppIntegrationSettingsSection_showWhen_accessor_storage, "f"); }
    set showWhen(value) { __classPrivateFieldSet(this, _AutomatorAppIntegrationSettingsSection_showWhen_accessor_storage, value, "f"); }
    /**
     * Called when the element is connected to the DOM
     *
     * @return {void}
     */
    connectedCallback() {
        super.connectedCallback();
        // If we have state configuration, set up listeners and check initial state
        if (this.state && this.showWhen) {
            this.setupStateListeners();
            this.checkInitialState();
        }
    }
    /**
     * Called when the element is disconnected from the DOM
     *
     * @return {void}
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        // Clean up listeners
        if (this.state) {
            this.removeStateListeners();
        }
    }
    /**
     * Set up state change listeners
     *
     * @return {void}
     */
    setupStateListeners() {
        // Listen for centralized state change events
        listenForEvent('state-change', this.handleStateChange.bind(this));
    }
    /**
     * Remove state listeners
     *
     * @return {void}
     */
    removeStateListeners() {
        removeEventListener('state-change', this.handleStateChange.bind(this));
    }
    /**
     * Check initial state using collectFormData
     *
     * @return {void}
     */
    checkInitialState() {
        // Find the form that contains this section
        const form = this.closest('form');
        if (!form) {
            return;
        }
        // Collect all form data to get current state values
        const formData = collectFormData(form, { includeHidden: true });
        // Get the current state value for our state name
        const currentState = formData.get(this.state) || '';
        // Set initial visibility
        this.visible = currentState === this.showWhen;
    }
    /**
     * Handle state changes
     *
     * @param {CustomEvent<IntegrationSettingsEvent>} event - The state change event
     *
     * @return {void}
     */
    handleStateChange(event) {
        // Type guard to ensure this is a state-change event
        if (event.detail.action !== 'state-change') {
            return;
        }
        const stateData = event.detail.data;
        const { state, value } = stateData;
        // Check if this state change is for our state
        if (state === this.state) {
            this.visible = value === this.showWhen;
        }
    }
    /**
     * Renders the component's template
     *
     * @return {TemplateResult} The template for the component
     */
    render() {
        if (!this.visible) {
            return html ``;
        }
        const classes = [
            'app-integration-settings-section',
            this.sectionType ? `app-integration-settings-section--${this.sectionType}` : '',
            this.cssClass
        ].filter(Boolean).join(' ');
        return html `
			<div class="${classes}">
				<slot></slot>
			</div>
		`;
    }
};
_AutomatorAppIntegrationSettingsSection_visible_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsSection_sectionType_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsSection_cssClass_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsSection_state_accessor_storage = new WeakMap();
_AutomatorAppIntegrationSettingsSection_showWhen_accessor_storage = new WeakMap();
// Define style
AutomatorAppIntegrationSettingsSection.styles = css `${unsafeCSS(styles)}`;
__decorate([
    property({ type: Boolean })
], AutomatorAppIntegrationSettingsSection.prototype, "visible", null);
__decorate([
    property({ type: String, attribute: 'section-type' })
], AutomatorAppIntegrationSettingsSection.prototype, "sectionType", null);
__decorate([
    property({ type: String, attribute: 'class' })
], AutomatorAppIntegrationSettingsSection.prototype, "cssClass", null);
__decorate([
    property({ type: String, attribute: 'state' })
], AutomatorAppIntegrationSettingsSection.prototype, "state", null);
__decorate([
    property({ type: String, attribute: 'show-when' })
], AutomatorAppIntegrationSettingsSection.prototype, "showWhen", null);
AutomatorAppIntegrationSettingsSection = __decorate([
    customElement('uap-app-integration-settings-section')
], AutomatorAppIntegrationSettingsSection);
export { AutomatorAppIntegrationSettingsSection };
